import { generatePath } from 'react-router-dom';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { extendedCommissionSchema, TExtendedCommission } from '@qapline/core/commission';
import { config } from 'config';
import {
    type CommissionsPageSchema,
    commissionsPageSchema,
    CommissionsTableSchema,
    CommissionSummarySchema,
    PostEnquiryEmailResponse,
    SendInquirySchema,
    SendUniversalOrderEmailSchema,
    TListCommissionNumberResponseBody,
    TListCommissionNumbersRequestBody,
    TPostDeliveryConfirmationEmailRequestBody,
    TPostDeliveryConfirmationEmailResponseBody,
    TPostLoadingConfirmationEmailRequestBody,
    TPostLoadingConfirmationEmailResponseBody,
    TPostNeutralizationConfirmationEmailRequestBody,
    TPostNeutralizationConfirmationEmailResponseBody,
    TPostOrderCarrierConfirmationEmailRequestBody,
    TPostOrderCarrierConfirmationEmailResponseBody,
    TPostOrderConfirmationEmailRequestBody,
    TPostOrderConfirmationEmailResponseBody,
    TQaplineCreateCommissionRequestBody,
} from 'modules/commissions';
import { QueryParams } from 'types/query-params';

export interface Message {
    event: 'invalidate';
    data: 'Carriers';
}

export const commissionsApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        getCommissions: builder.query<CommissionsPageSchema, QueryParams<CommissionsTableSchema>>({
            query: (params) => ({
                url: config.api.endpoints.commission.list,
                params,
            }),
            providesTags: () => [ApiCacheTags.commission.list],
            transformResponse: (res: CommissionsPageSchema) => {
                const validatedSchema = commissionsPageSchema().safeParse(res);
                // used for back compatibility; almost all commissions are incorrect
                if (!validatedSchema.success) {
                    console.warn('Validation failed in getCommissions');
                }
                return res;
            },
        }),
        getCommissionSummary: builder.query<CommissionSummarySchema, QueryParams<CommissionsTableSchema>>({
            query: (params) => ({
                url: config.api.endpoints.commissionSummary,
                params,
            }),
            providesTags: () => [ApiCacheTags.commission.summary],
        }),
        postNewCommission: builder.mutation<TExtendedCommission, TQaplineCreateCommissionRequestBody>({
            query: (body) => ({
                url: config.api.endpoints.commission.create,
                method: 'POST',
                body,
            }),
            transformResponse: (response) => extendedCommissionSchema.parse(response),
            invalidatesTags: (_res, error) =>
                error ? [] : [ApiCacheTags.commission.list, ApiCacheTags.commission.summary],
        }),
        deleteCommission: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: generatePath(config.api.endpoints.commission.delete, { commissionId: id.toString() }),
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) =>
                error ? [] : [ApiCacheTags.commission.list, ApiCacheTags.commission.summary],
        }),
        // createOrderCarrier - Objednávka na dopravce
        postOrderCarrierPdf: builder.query<
            Blob,
            {
                body: {
                    commissionId: number;
                    lang: string;
                    earlyPayOffered?: boolean;
                    earlyPayDiscount?: number;
                };
                aborter?: () => AbortSignal;
            }
        >({
            providesTags: () => [ApiCacheTags.commission.pdf],
            query: ({ body, aborter }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderCarrierPdf,
                    method: 'POST',
                    body,
                    signal,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postOrderCarrierConfirmationEmailOld: builder.query({
            providesTags: () => [ApiCacheTags.commission.email],
            query: ({ body }: { body: SendUniversalOrderEmailSchema }) => ({
                url: config.api.endpoints.orderCarrierConfirmationEmail,
                method: 'POST',
                body,
            }),
        }),
        postOrderCarrierConfirmationEmail: builder.mutation<
            TPostOrderCarrierConfirmationEmailResponseBody,
            TPostOrderCarrierConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderCarrierConfirmationEmail,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: (_res, error, body) =>
                error
                    ? []
                    : [
                          { type: ApiCacheTags.commission.item, id: body.commissionId },
                          ApiCacheTags.commission.list,
                          ApiCacheTags.commission.summary,
                      ],
        }),
        //  createOrderConfirmation - Potvrzení objednávky
        postOrderConfirmationEmail: builder.mutation<
            TPostOrderConfirmationEmailResponseBody,
            TPostOrderConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderConfirmationEmail,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: (_res, error) =>
                error ? [] : [ApiCacheTags.commission.list, ApiCacheTags.commission.summary],
        }),
        // createLoadingConfirmation - Potvrzení nakládky
        postLoadingPdf: builder.query<
            Blob,
            { body: { commissionId: number; lang: string; locationId: number }; aborter?: () => AbortSignal }
        >({
            providesTags: () => [ApiCacheTags.commission.pdf],
            query: ({ body, aborter }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderLoadingPdf,
                    method: 'POST',
                    signal,
                    body,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postLoadingConfirmationEmail: builder.mutation<
            TPostLoadingConfirmationEmailResponseBody,
            TPostLoadingConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderLoadingConfirmationEmail,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: (_res, error, body) =>
                error
                    ? []
                    : [
                          { type: ApiCacheTags.commission.item, id: body.commissionId },
                          ApiCacheTags.commission.list,
                          ApiCacheTags.commission.summary,
                      ],
        }),
        // createDeliveryConfirmation - Potvrzení doručení zboží
        postDeliveryPdf: builder.query<
            Blob,
            { body: { commissionId: number; lang: string; locationId: number }; aborter?: () => AbortSignal }
        >({
            providesTags: () => [ApiCacheTags.commission.pdf],
            query: ({ body, aborter }) => {
                const signal = aborter?.();
                return {
                    url: config.api.endpoints.orderDeliveryPdf,
                    method: 'POST',
                    body,
                    signal,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postDeliveryConfirmationEmail: builder.mutation<
            TPostDeliveryConfirmationEmailResponseBody,
            TPostDeliveryConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderDeliveryConfirmationEmail,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: (_res, error, body) =>
                error
                    ? []
                    : [
                          { type: ApiCacheTags.commission.item, id: body.commissionId },
                          ApiCacheTags.commission.list,
                          ApiCacheTags.commission.summary,
                      ],
        }),
        postNeutralizationPdf: builder.query<
            Blob,
            { body: { commissionId: number; lang: string; locationId: number }; aborter?: () => AbortSignal }
        >({
            providesTags: ['Commissions'],
            query: ({ body, aborter }) => {
                aborter?.();
                return {
                    url: config.api.endpoints.orderDischargePdf,
                    method: 'POST',
                    body,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),
        postNeutralizationConfirmationEmail: builder.mutation<
            TPostNeutralizationConfirmationEmailResponseBody,
            TPostNeutralizationConfirmationEmailRequestBody
        >({
            query: (body) => ({
                url: config.api.endpoints.orderDischargeConfirmationEmail,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: (_res, error, body) =>
                error
                    ? []
                    : [
                          { type: ApiCacheTags.commission.item, id: body.commissionId },
                          ApiCacheTags.commission.list,
                          ApiCacheTags.commission.summary,
                      ],
        }),
        postInquiryEmail: builder.mutation<PostEnquiryEmailResponse, { body: SendInquirySchema }>({
            invalidatesTags: (_result, _error, { body }) => [{ type: 'CommissionItem', id: body.commissionId }],
            query: ({ body }) => ({
                url: config.api.endpoints.carrierInquiry,
                method: 'POST',
                body,
            }),
        }),
        listCommissionNumbers: builder.query<TListCommissionNumberResponseBody, TListCommissionNumbersRequestBody>({
            query: ({ search, age }) => {
                return {
                    url: config.api.endpoints.commissionListNumbers,
                    method: 'GET',
                    params: {
                        ...(search ? { search } : {}),
                        ...(age ? { age } : {}),
                    },
                };
            },
            providesTags: (_, __, arg) => [{ type: 'CommissionNumbers', id: arg.search }],
        }),
    }),
});

export const {
    useGetCommissionsQuery,
    usePostNewCommissionMutation,
    useDeleteCommissionMutation,
    useLazyPostOrderCarrierPdfQuery,
    usePostOrderCarrierConfirmationEmailMutation,
    usePostOrderConfirmationEmailMutation,
    useLazyPostLoadingPdfQuery,
    useGetCommissionSummaryQuery,
    usePostLoadingConfirmationEmailMutation,
    useLazyPostDeliveryPdfQuery,
    usePostDeliveryConfirmationEmailMutation,
    useLazyPostNeutralizationPdfQuery,
    usePostNeutralizationConfirmationEmailMutation,
    usePostInquiryEmailMutation,
    useListCommissionNumbersQuery,
    useLazyListCommissionNumbersQuery,
} = commissionsApi;
